import { gql } from '@apollo/client';
import { Alert, Badge, Button, Col, Form, Input, Row } from 'antd';
import FormDependency from 'components/FormDependency';
import { useMutation } from 'hooks/apollo';
import React, { useEffect } from 'react';
import theme from 'shared/constants/theme.json';
import ActiveInput from 'components/ActiveInput';
import { DeleteFilled, NumberOutlined, PlusOutlined } from '@ant-design/icons';
import useDataSource from 'hooks/useDataSource';
import Page from 'components/Page';
import { FormattedMessage } from 'react-intl';
import { KB_CUSTOM_DIMENSIONS } from 'shared/constants/urls';
import messages from '../messages';
import { getUpdateWebsiteVariables } from './updateWebsiteVariables';

const FIELDS = `
  id
  matomoId
  matomoVisitDimensions {
    id
    index
    name
    active
  }
  matomoCustomDimensionScopes {
    id
    numSlotsAvailable    
  }
`;

const UPDATE_WEBSITE = gql`
  mutation($id: ID!, $input: WebsiteInput!) {
    updateWebsite(id: $id, input: $input) {
      ${FIELDS}
    }
  }
`;

const VisitDimensionSettings = () => {
  const { dataSource, loading, refetch } = useDataSource({ websiteFields: FIELDS });
  const [updateWebsite, { loading: updating }] = useMutation(UPDATE_WEBSITE, { onCompleted: refetch });
  const [form] = Form.useForm();

  const { website } = dataSource || {};

  useEffect(() => {
    form.resetFields();
  }, [website]);

  const onSubmit = (values) => updateWebsite({ variables: getUpdateWebsiteVariables(website, values) });

  const { numSlotsAvailable: visitSlotsAvailable = 0 } = website?.matomoCustomDimensionScopes?.find(s => s.id === 'visit') || {};
  const visitDimensionCount = website?.matomoVisitDimensions?.length || 0;

  return (
    <Page loading={loading}>
      <Alert
        message={(
          <a href={KB_CUSTOM_DIMENSIONS} target="_blank">Read more about custom dimensions in our guide.</a>
        )}
        type="info"
        showIcon
      />
      <Page.Content title="Visit dimensions" extra={<Badge style={{ backgroundColor: theme[(visitSlotsAvailable >= 0 && visitDimensionCount >= visitSlotsAvailable) ? 'error-color' : 'success-color'] }} count={`${visitDimensionCount} / ${visitSlotsAvailable}`} showZero />}>
        <Form form={form} onFinish={onSubmit} initialValues={website} layout="vertical">
          <Form.Item name="id" hidden>
            <input type="hidden" />
          </Form.Item>
          <p><FormattedMessage {...messages.matomoVisitDimensionsDescription} /></p>
          <Form.List name="matomoVisitDimensions">
            {(fields, { add, remove }) => (
              <>
                {fields.map((field) => (
                  <Row key={field.key} gutter={16}>
                    <Col span={3}>
                      <Form.Item name={[field.name, 'index']}>
                        <Input prefix={<NumberOutlined />} disabled />
                      </Form.Item>
                    </Col>
                    <Col span={19}>
                      <Form.Item name={[field.name, 'name']} rules={[{ required: true, message: 'Dimension name is required' }]}>
                        <Input suffix={<FormDependency name={['matomoVisitDimensions', field.name, 'id']} show={v => !v}><DeleteFilled className="ant-btn-dangerous" onClick={() => remove(field.name)} /></FormDependency>} placeholder="Name" />
                      </Form.Item>
                    </Col>
                    <Col span={2}>
                      <Form.Item name={[field.name, 'active']}>
                        <ActiveInput />
                      </Form.Item>
                    </Col>
                  </Row>
                ))}
                <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />} disabled={!website || fields.length >= visitSlotsAvailable}>
                  Add visit dimension
                </Button>
              </>
            )}
          </Form.List>

          <Page.Footer>
            <Button type="primary" htmlType="submit" loading={updating}>Save</Button>
          </Page.Footer>
        </Form>
      </Page.Content>
    </Page>
  );
};

export default VisitDimensionSettings;
